/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AutocompleteItemDto, ID, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet } from '@gh/core-data';
import { ContactCustomerType } from '../contacts/model/contact-details-customer.dto';
import { Priority, Stage } from '../leads/lead.dto';
import { SalesFolderStatus } from '../leads/sales-folder.dto';

export const EmailCampaignStatus = createEnum('CAMPAIGN_STATUS', {
  Created: 1,
  Sent: 2,
  Cancelled: 3,
  Deleted: 4,
});

export const EmailCampaignAccountType = createEnum('PLAN_TYPE', {
  ForeverFree: 'forever_free',
});

export type EmailCampaignStatus = EnumSet<typeof EmailCampaignStatus>;
export type EmailCampaignAccountType = EnumSet<typeof EmailCampaignAccountType>;

export type EmailCampaignListItemDto = WithVersion<{
  campaignId: ID;
  name: string;
  status: string;
  statusValue: EmailCampaignStatus;
  performedDate: Date;
  addedDate: Date;
  externalId: ID;
}>;

export type EmailCampaignFilterDto = {
  showPerformed: boolean;
  showCancelled: boolean;
};

export type EmailCampaignReportDto = {
  title: string;
  subjectLine: string;
  deliveryDate: Date;
  recipientsTotal: number;
  successfulDeliveries: number;
  bouncesCount: number;
  bouncesPct: number;
  timesForwarded: number;
  forwarderOpens: number;
  recipientsWhoOpenedCount: number;
  recipientsWhoOpenedPct: number;
  totalOpens: number;
  lastOpenedDate: Date;
  recipientsWhoClickedCount: number;
  recipientsWhoClickedPct: number;
  totalClicks: number;
  lastClickDate: Date;
  totalUnsubscribs: number;
  totalAbuseComplains: number;
  timesLikedOnFacebook: number;
  clicksByUrl: EmailCampaignReportClickDto[];
};

export type EmailCampaignReportClickDto = {
  url: string;
  totalClicks: number;
  uniqueClicks: number;
};

export type EmailCampaignRecipientDto = {
  contactId: ID;
  contactName: string;
  contactEmail: string;
  externalId: ID;
  communicationId: ID;
  contactFirstName: string;
  contactLastName: string;
  contactType: any;
};

export type EmailCampaignDetailsDto = WithVersion<{
  campaignId: ID;
  name: string;
  status: EmailCampaignStatus;
  offices: AutocompleteItemDto[];
  masterZones: AutocompleteItemDto[];
  salespersons: AutocompleteItemDto[];
  communities: AutocompleteItemDto[];
  recipients: EmailCampaignRecipientDto[];
  paragraph: string;
  externalId: ID;
  priorities: Priority[];
  salesFolderStages: Stage[];
  externalTemplateId: ID;
  externalTemplateName: string;
  performedDate: Date;
  typeOfCustomer?: ContactCustomerType;
  salesFolderStatus?: SalesFolderStatus;
  externalListId: ID;
  pricingPlanType: string;
}>;

export type EmailCampaignAccountInfoDto = {
  pricing_plan_type: EmailCampaignAccountType;
};

export type EmailCampaignRecipientFilterDto = {
  masterZoneIds: ID[];
  officeIds: ID[];
  communityIds: ID[];
  salesPersonIds: ID[];
  salesFolderStatus?: SalesFolderStatus;
  salesFolderStages: Stage[];
  customerType?: ContactCustomerType;
  priorities: Priority[];
};

export type EmailCampaignTemplateDto = AutocompleteItemDto;
